import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, History, Plus, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { ukDay, ukTime } from "@/lib/time";
import { ConnieMark } from "./ConnieMark";
import { ConnieText } from "./ConnieText";
import { useConnie } from "./useConnie";

/** The full Ask Connie surface — empty-state hero, streamed thread, composer. Shared by both
    portals and the Hub control room. */
export function ConnieChat({ height = "calc(100dvh - 250px)", testPrefix = "connie" }) {
  const c = useConnie();
  const scroller = useRef(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = c.messages.length ? el.scrollHeight : 0;
  }, [c.messages]);

  if (c.error) return <div className="empty" data-testid={`${testPrefix}-error`}>{c.error}</div>;
  if (!c.me) return <div className="empty" data-testid={`${testPrefix}-loading`}>Waking Connie up…</div>;

  const streaming = c.messages.some((m) => m.streaming);
  const last = c.messages[c.messages.length - 1];
  const state = !streaming ? "idle" : last?.text ? "speaking" : "thinking";

  return (
    <div className="card connie-chat" style={{ height, minHeight: 520, overflow: "hidden" }} data-testid={`${testPrefix}-chat`}>
      <Bar c={c} state={state} onHistory={() => setShowHistory((s) => !s)} testPrefix={testPrefix} />
      {showHistory && <HistoryList onPick={(id) => { setShowHistory(false); c.open(id); }} testPrefix={testPrefix} />}
      <div ref={scroller} className="connie-scroll" aria-live="polite" data-testid={`${testPrefix}-thread`}>
        {!c.me.enabled ? <Paused /> : c.messages.length === 0
          ? <Welcome me={c.me} onPick={c.send} testPrefix={testPrefix} />
          : (
            <div style={{ maxWidth: 780, margin: "0 auto", padding: "26px 22px 10px", display: "flex", flexDirection: "column", gap: 18 }}>
              {c.messages.map((m) => <Message key={m.id} m={m} testPrefix={testPrefix} />)}
            </div>
          )}
      </div>
      {c.me.enabled && <Composer busy={c.busy} onSend={c.send} testPrefix={testPrefix} />}
    </div>
  );
}

function Bar({ c, state, onHistory, testPrefix }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
      <ConnieMark size={34} state={state} testId={`${testPrefix}-bar-mark`} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ font: '800 15px/1.1 Archivo', letterSpacing: "-.01em" }}>Connie</div>
        <div style={{ fontSize: 11.5, color: "var(--mut)", marginTop: 3 }} className="truncate" data-testid={`${testPrefix}-status`}>
          {state === "thinking" ? "Thinking…" : state === "speaking" ? "Writing…" : c.me.enabled ? "Here for you, any time" : "Taking a short break"}
        </div>
      </div>
      <button className="btn btn-ghost btn-sm connie-bar-btn" onClick={onHistory} aria-label="Earlier chats" data-testid={`${testPrefix}-history-btn`}>
        <History /> <span>Earlier chats</span>
      </button>
      <button className="btn btn-ghost btn-sm connie-bar-btn" onClick={c.fresh} disabled={c.busy} aria-label="New chat" data-testid={`${testPrefix}-new-btn`}>
        <Plus /> <span>New chat</span>
      </button>
    </div>
  );
}

function HistoryList({ onPick, testPrefix }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/connie/me/sessions").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  return (
    <div style={{ borderBottom: "1px solid var(--line)", background: "var(--canvas)", maxHeight: 220, overflowY: "auto", padding: 10 }}
         data-testid={`${testPrefix}-history`}>
      {items === null ? <div className="cell-sub" style={{ padding: 8 }}>Loading…</div>
        : items.length === 0 ? <div className="cell-sub" style={{ padding: 8 }}>No earlier chats yet.</div>
          : items.map((s) => (
            <button key={s.id} onClick={() => onPick(s.id)} data-testid={`${testPrefix}-history-${s.id}`}
                    style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 12, textAlign: "left",
                      padding: "9px 10px", borderRadius: 10, background: "transparent", border: 0, cursor: "pointer" }}
                    className="hover:bg-white">
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", minWidth: 0 }} className="truncate">{s.title || "New chat"}</span>
              <span style={{ fontSize: 11.5, color: "var(--mut)", whiteSpace: "nowrap" }}>{ukDay(s.updated_at)} · {ukTime(s.updated_at)}</span>
            </button>
          ))}
    </div>
  );
}

function Welcome({ me, onPick, testPrefix }) {
  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 22px 24px", textAlign: "center" }} data-testid={`${testPrefix}-welcome`}>
      <div style={{ display: "grid", placeItems: "center", marginBottom: 22 }}><ConnieMark size={96} testId={`${testPrefix}-hero-mark`} /></div>
      <div className="eyebrow" style={{ marginBottom: 10 }}>Connie · Connexis</div>
      <h2 style={{ font: '800 clamp(26px,3.4vw,38px)/1.08 Archivo', letterSpacing: "-.03em", margin: 0 }}>
        Hi {me.first_name}, I&apos;m Connie.
      </h2>
      <p style={{ color: "var(--mut)", fontSize: 15, lineHeight: 1.6, margin: "14px auto 0", maxWidth: 520 }}>
        I know Connexis inside out — your cases, how everything works, and how to get more over the line.
        Ask me anything, or ask me to chase something for you.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10, marginTop: 24, textAlign: "left" }}>
        {(me.suggestions || []).map((s, i) => (
          <button key={s} className="connie-prompt connie-in" style={{ animationDelay: `${i * 60}ms` }}
                  onClick={() => onPick(s)} data-testid={`${testPrefix}-suggestion-${i}`}>{s}</button>
        ))}
      </div>
    </div>
  );
}

function Message({ m, testPrefix }) {
  if (m.role === "user") {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }} className="connie-in" data-testid={`${testPrefix}-msg-user`}>
        <div className="connie-me" style={{ maxWidth: "78%" }}>{m.text}</div>
      </div>
    );
  }
  const live = m.live || [];
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }} className="connie-in" data-testid={`${testPrefix}-msg-connie`}>
      <ConnieMark size={30} state={m.streaming ? (m.text ? "speaking" : "thinking") : "idle"} orbit={false} testId={`${testPrefix}-msg-mark`} />
      <div style={{ minWidth: 0, flex: 1, maxWidth: "88%" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--accent-text)", margin: "4px 0 7px" }}>
          Connie · Connexis
        </div>
        {live.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {live.map((t, i) => (
              <span key={`${t.name}-${i}`} className={`connie-tool${!m.streaming || i < live.length - 1 || m.text ? " done" : ""}`}
                    data-testid={`${testPrefix}-tool-${t.name}`}><i />{t.label}</span>
            ))}
          </div>
        )}
        <div className="connie-bubble" style={m.failed ? { borderColor: "rgba(200,21,31,.3)" } : undefined}>
          {m.text ? <ConnieText text={m.text} /> : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--mut)", fontSize: 13 }}>
              <Loader2 className="size-3.5 animate-spin" /> {live.length ? live[live.length - 1].label : "Thinking"}…
            </span>
          )}
          {m.streaming && m.text && <span className="connie-caret" />}
        </div>
      </div>
    </div>
  );
}

function Composer({ busy, onSend, testPrefix }) {
  const [v, setV] = useState("");
  const ta = useRef(null);
  const go = () => { if (!v.trim() || busy) return; onSend(v); setV(""); };
  useEffect(() => {
    const el = ta.current;
    if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 160)}px`; }
  }, [v]);
  return (
    <div style={{ padding: "12px 18px 14px", borderTop: "1px solid var(--line)", background: "linear-gradient(180deg,#fff,#FAFBFD)" }}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <div className="connie-composer">
          <textarea ref={ta} rows={1} value={v} onChange={(e) => setV(e.target.value)} placeholder="Ask Connie anything about Connexis…"
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); go(); } }}
                    data-testid={`${testPrefix}-input`} />
          <button className="connie-send" onClick={go} disabled={busy || !v.trim()} aria-label="Send" data-testid={`${testPrefix}-send`}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", marginTop: 9, fontSize: 11.5, color: "var(--mut)" }}
             className="connie-trust" data-testid={`${testPrefix}-trust-line`}>
          <ShieldCheck className="size-3.5" style={{ color: "var(--green)" }} />
          Connie never shares lender or introducer identities. Conversations are saved so you can pick up later.
        </div>
      </div>
    </div>
  );
}

const Paused = () => (
  <div style={{ maxWidth: 520, margin: "0 auto", padding: "70px 22px", textAlign: "center" }} data-testid="connie-paused">
    <div style={{ display: "grid", placeItems: "center", marginBottom: 24 }}><ConnieMark size={86} orbit={false} /></div>
    <h2 style={{ font: '800 24px/1.15 Archivo', margin: 0 }}>Connie is taking a short break</h2>
    <p style={{ color: "var(--mut)", marginTop: 10, lineHeight: 1.6 }}>
      The Connexis desk is here as usual — message us from <a href="messages" style={{ color: "var(--accent-text)", fontWeight: 700 }}>Messages</a>.
    </p>
  </div>
);

export default ConnieChat;
